import { MESSAGE } from "@/constants/message";

import { UploadDatasetDialogType } from "./UploadDialog";

type ErrorDisplay = {
  title: string;
  description: string;
};

type UploadError = Error & {
  message: { code: number; message: string };
};

const SHAPEFILE_ERROR_MAP: {
  matcher: RegExp;
  display: ErrorDisplay | ((message: string) => ErrorDisplay);
}[] = [
  {
    matcher: /multiple file upload only for shapefiles/i,
    display: {
      title: MESSAGE.SHAPEFILE_ERROR_UNSUPPORTED_MULTIPLE_TITLE,
      description: MESSAGE.SHAPEFILE_ERROR_UNSUPPORTED_MULTIPLE_DESCRIPTION
    }
  },
  {
    matcher:
      /missing required file(?:\(s\))?\s+\.\w+(?:\s+and\s+\.\w+)?(?:\s+for Shapefile(?:\s+in zip)?)?/i,

    display: (message: string) => {
      const matches = message.match(/\.\w+/g);

      const firstFile = matches?.[0] ?? "required file";
      const secondFile = matches?.[1];

      return {
        title: "Cannot Upload Files",
        description: secondFile
          ? `Missing required ${firstFile} and ${secondFile} file. Please try again.`
          : `Missing required ${firstFile} file. Please try again.`
      };
    }
  },
  {
    matcher: /multiple shapefile sets detected/i,
    display: {
      title: MESSAGE.SHAPEFILE_ERROR_NOT_SUPPORTED_MULTIPLE_ONETIME_TITLE,
      description:
        MESSAGE.SHAPEFILE_ERROR_NOT_SUPPORTED_MULTIPLE_ONETIME_DESCRIPTION
    }
  },
  {
    matcher: /all shapefile files must share the same base name\./i,
    display: {
      title: MESSAGE.SHAPEFILE_ERROR_SAMENAME_TITLE,
      description: MESSAGE.SHAPEFILE_ERROR_SAMENAME_DESCRIPTION
    }
  }
];

const getShapefileErrorDisplay = (message: string): ErrorDisplay | null => {
  const match = SHAPEFILE_ERROR_MAP.find(({ matcher }) =>
    matcher.test(message)
  );
  if (!match) return null;
  return typeof match.display === "function"
    ? match.display(message)
    : match.display;
};

export const getUploadErrorDisplay = (
  error: Error,
  uploadType: UploadDatasetDialogType,
  errorMessages: Record<UploadDatasetDialogType, string>
): ErrorDisplay => {
  const customError = error as UploadError;
  const message = customError.message?.message || "An error occurred";
  const shapefileError = getShapefileErrorDisplay(message);

  return {
    title: shapefileError?.title ?? "Something Went Wrong",
    description: shapefileError?.description ?? errorMessages[uploadType]
  };
};
